import React, { useState, useEffect, useRef, type ReactElement, type RefCallback } from 'react';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import type { DropdownTriggerParams } from '@atlassian/jira-development-summary-common/src/common/types.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { createCommitDropdownContentEntrypoint } from './create-commit-dropdown-content/entrypoint.tsx';

export type Props = {
	getDropdownTrigger: (props: DropdownTriggerParams) => ReactElement;
	onOpenDropdown?: (arg1: { isOpen: boolean }) => void;
};

const entryPointParams = {};
export const CreateCommitDropdown = ({ onOpenDropdown, getDropdownTrigger }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setIsOpen] = useState(false);
	const previousOpen = useRef(false);
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		createCommitDropdownContentEntrypoint,
		entryPointParams,
	);
	const triggerRef = useEntryPointButtonTrigger(entryPointActions, !isOpen);

	useEffect(() => {
		if (previousOpen.current !== isOpen) {
			previousOpen.current = isOpen;
			isOpen &&
				fireUIAnalytics(createAnalyticsEvent({}), 'dropdown opened', 'commitCreateDropdown');
			!isOpen &&
				fireUIAnalytics(createAnalyticsEvent({}), 'dropdown closed', 'commitCreateDropdown');
			onOpenDropdown && onOpenDropdown({ isOpen });
		}
	}, [createAnalyticsEvent, isOpen, onOpenDropdown]);

	return (
		<Popup
			id="create-commit-dropdown"
			testId="development-summary-commit.ui.create-commit-dropdown.popup"
			zIndex={layers.modal + 1}
			placement="bottom-end"
			messageId="development-summary-commit.ui.create-commit-dropdown.popup"
			messageType="transactional"
			shouldFlip
			trigger={(triggerProps) =>
				getDropdownTrigger({
					...triggerProps,
					isSelected: isOpen,
					onClick: () => setIsOpen(!isOpen),
					ref: (element) => {
						triggerRef(element);
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						(triggerProps.ref as RefCallback<HTMLElement>)(element);
					},
				})
			}
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			content={() => (
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="dev-panel-create-commit-dropdown-content"
					packageName="jira-development-summary-commit"
					fallback={null}
					runtimeProps={{}}
				/>
			)}
		/>
	);
};

export default CreateCommitDropdown;
